import { usePage } from '@inertiajs/vue3'
import { useEvaluationStore } from '@/stores/useEvaluationStore'

/**
 * RED-279: single source for resolving which ML models a prediction uses.
 *
 * Policy: an explicit in-session pick (ModelSelector / settings popover) wins
 * as long as that model is still served; otherwise the backend's pinned
 * default (`ml_models.is_default`, shared via the `modelDefaults` page prop);
 * newest-by-created_at only as a last resort when no default is known — and
 * never for the EBM (RED-355 B3): with no pinned EBM the request carries none
 * and the backend fails closed instead of serving an unpromoted bundle.
 *
 * Replaces three drifted copies of "resolve to latest available" — defaulting
 * to the newest model is how expert predictions ended up on an uncalibrated
 * bundle that returns no "Ruumide seisukord" adjustment group.
 */
export function useModelResolution() {
  const page = usePage()
  const evaluationStore = useEvaluationStore()

  const resolveModel = (type, current, pickedByUser) => {
    const models = (page.props?.modelsAvailable?.models ?? []).filter(
      m => m.type === type,
    )
    if (!models.length) {
      return current
    }

    const isAvailable = value => models.some(m => m.model === value)

    if (pickedByUser && isAvailable(current)) {
      return current
    }

    const preferred = page.props?.modelDefaults?.[type]
    if (isAvailable(preferred)) {
      return preferred
    }

    if (type === 'ebm') {
      return null
    }

    return [...models].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    )[0].model
  }

  /**
   * Sync the evaluation store's model selections with the resolution policy.
   * Call before every predict/refresh and on mount of the model controls.
   */
  const ensureModelsResolved = () => {
    evaluationStore.modelType = resolveModel(
      'ebm',
      evaluationStore.modelType,
      evaluationStore.modelTypePickedByUser,
    )
    evaluationStore.similarityPriceModel = resolveModel(
      'catboost',
      evaluationStore.similarityPriceModel,
      evaluationStore.similarityPriceModelPickedByUser,
    )
  }

  return { ensureModelsResolved }
}
